// Rich Text Editor with save status indicator
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Image } from '@tiptap/extension-image';
import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import { Placeholder } from '@tiptap/extension-placeholder';
import { TextAlign } from '@tiptap/extension-text-align';
import { common, createLowlight } from 'lowlight';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotes } from '../../contexts/NotesContext.jsx';
import { useFolders } from '../../contexts/FoldersContext.jsx';
import { useTags } from '../../contexts/TagsContext.jsx';
import Icon from '../common/Icon';
import ExportDropdown from '../common/ExportDropdown';
import Toolbar from './Toolbar.jsx';
import AIAssistant from './AIAssistant.jsx';
import FolderPicker from '../Modals/FolderPicker';
import TagPicker from '../Modals/TagPicker';
import './Editor.css';

const lowlight = createLowlight(common);

export default function Editor({ note, onShare }) {
    const { updateNote, saveStatus, syncNotes } = useNotes();
    const { folders } = useFolders();
    const { tags, createTag } = useTags();
    const saveTimeoutRef = useRef(null);
    const [lastSaved, setLastSaved] = useState(null);

    // Folder and tag selection state
    const [selectedFolderId, setSelectedFolderId] = useState(note?.folderId || null);
    const [selectedTagIds, setSelectedTagIds] = useState(note?.tagIds || []);

    // Refs for tracking local vs remote updates
    const isLocalUpdateRef = useRef(false);
    const lastRemoteContentRef = useRef(note.content);
    const currentNoteKeyRef = useRef(note.key);
    const noteChangedRef = useRef(false);
    // Flag to prevent saves during note switching/loading
    const isSwitchingNoteRef = useRef(false);

    // Reset refs when note key changes (different note selected)
    useEffect(() => {
        if (note.key !== currentNoteKeyRef.current) {
            currentNoteKeyRef.current = note.key;
            lastRemoteContentRef.current = note.content;
            isLocalUpdateRef.current = false;
            noteChangedRef.current = true; // Flag note switch
            isSwitchingNoteRef.current = true; // Prevent saves during switch
        }
    }, [note.key, note.content]);

    // Update folder/tag selection when note changes
    useEffect(() => {
        setSelectedFolderId(note?.folderId || null);
        setSelectedTagIds(note?.tagIds || []);
    }, [note?.id, note?.key]);

    // Parse content for editor
    const initialContent = parseContent(note.content);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                codeBlock: false,
            }),
            Table.configure({
                resizable: true,
                HTMLAttributes: { class: 'note-table' },
            }),
            TableRow,
            TableCell,
            TableHeader,
            Image.configure({
                inline: true,
                allowBase64: true,
                HTMLAttributes: { class: 'note-image' },
            }),
            CodeBlockLowlight.configure({
                lowlight,
                HTMLAttributes: { class: 'code-block' },
            }),
            TextStyle,
            Color,
            Highlight.configure({
                multicolor: true,
            }),
            Placeholder.configure({
                placeholder: 'Start typing your notes...',
            }),
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
        ],
        content: initialContent,
        editorProps: {
            attributes: {
                class: 'prose',
            },
        },
        onUpdate: ({ editor }) => {
            // Skip saves during note switching to prevent unwanted timestamp updates
            if (isSwitchingNoteRef.current) {
                return;
            }
            
            // Debounce save
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
            saveTimeoutRef.current = setTimeout(() => {
                // Double-check we're not still switching notes
                if (isSwitchingNoteRef.current) {
                    return;
                }
                const json = editor.getJSON();
                const serialized = JSON.stringify({
                    version: 2,
                    content: json,
                    updatedAt: new Date().toISOString(),
                });
                // Mark as local update to prevent sync loop
                isLocalUpdateRef.current = true;
                lastRemoteContentRef.current = serialized;
                updateNote(note.key, serialized);
                setLastSaved(new Date());
            }, 800);
        },
    });

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, []);

    // Update content when note changes from remote (other devices)
    useEffect(() => {
        if (!editor) return;

        // Check if we just switched notes - force update if so
        const justSwitchedNotes = noteChangedRef.current;
        if (justSwitchedNotes) {
            noteChangedRef.current = false; // Reset the flag
        }

        // Skip if this is our own local update (after saving) and we didn't just switch notes
        if (isLocalUpdateRef.current && !justSwitchedNotes) {
            isLocalUpdateRef.current = false;
            return;
        }

        // Check if content actually changed from remote (skip this check if we just switched notes)
        if (!justSwitchedNotes) {
            // Use string comparison for initial check
            const noteContentStr = typeof note.content === 'string'
                ? note.content
                : JSON.stringify(note.content);
            const lastContentStr = typeof lastRemoteContentRef.current === 'string'
                ? lastRemoteContentRef.current
                : JSON.stringify(lastRemoteContentRef.current);

            if (noteContentStr === lastContentStr) return;
        }

        // Store the new remote content
        lastRemoteContentRef.current = note.content;

        const newContent = parseContent(note.content);
        const currentContent = editor.getJSON();

        // Deep compare parsed content to avoid unnecessary editor updates (skip if we just switched notes)
        const newContentStr = JSON.stringify(newContent);
        const currentContentStr = JSON.stringify(currentContent);

        if (justSwitchedNotes || newContentStr !== currentContentStr) {
            console.log('[Editor] Updating content from remote sync');

            // Save cursor position
            const { from, to } = editor.state.selection;

            // Update content without triggering onUpdate callback
            editor.commands.setContent(newContent, false);

            // Try to restore cursor position (only if not switching notes)
            if (!justSwitchedNotes) {
                try {
                    const maxPos = editor.state.doc.content.size;
                    const safeFrom = Math.min(from, maxPos);
                    const safeTo = Math.min(to, maxPos);
                    editor.commands.setTextSelection({ from: safeFrom, to: safeTo });
                } catch (e) {
                    // Cursor restore failed, that's OK
                }
            }
            
            // Reset the switching flag after content is loaded
            // Use setTimeout to ensure any pending onUpdate calls are skipped
            if (justSwitchedNotes) {
                setTimeout(() => {
                    isSwitchingNoteRef.current = false;
                }, 100);
            }
        }
    }, [editor, note.content, note.key]);

    // Manual save function
    const handleManualSave = useCallback(() => {
        if (!editor) return;

        const json = editor.getJSON();
        const serialized = JSON.stringify({
            version: 2,
            content: json,
            updatedAt: new Date().toISOString(),
        });
        updateNote(note.key, serialized, {
            folderId: selectedFolderId,
            tagIds: selectedTagIds,
        });
        setLastSaved(new Date());
    }, [editor, note.key, updateNote, selectedFolderId, selectedTagIds]);

    // Handle folder change
    const handleFolderChange = useCallback((folderId) => {
        setSelectedFolderId(folderId);
        // Auto-save folder assignment
        if (editor) {
            const json = editor.getJSON();
            const serialized = JSON.stringify({
                version: 2,
                content: json,
                updatedAt: new Date().toISOString(),
            });
            isLocalUpdateRef.current = true;
            updateNote(note.key, serialized, {
                folderId: folderId,
                tagIds: selectedTagIds,
            });
            setLastSaved(new Date());
        }
    }, [editor, note.key, updateNote, selectedTagIds]);

    // Handle tags change
    const handleTagsChange = useCallback((tagIds) => {
        setSelectedTagIds(tagIds);
        // Auto-save tags assignment
        if (editor) {
            const json = editor.getJSON();
            const serialized = JSON.stringify({
                version: 2,
                content: json,
                updatedAt: new Date().toISOString(),
            });
            isLocalUpdateRef.current = true;
            updateNote(note.key, serialized, {
                folderId: selectedFolderId,
                tagIds: tagIds,
            });
            setLastSaved(new Date());
        }
    }, [editor, note.key, updateNote, selectedFolderId]);

    const getTitle = useCallback(() => {
        if (note.key && note.key.startsWith('http')) {
            try {
                const url = new URL(note.key);
                return url.hostname + url.pathname;
            } catch {
                return note.key;
            }
        }
        return note.domain || 'Untitled';
    }, [note]);

    const handleTitleClick = useCallback(() => {
        if (note.key && note.key.startsWith('http')) {
            window.open(note.key, '_blank', 'noopener,noreferrer');
        }
    }, [note.key]);

    const isClickableUrl = note.key && note.key.startsWith('http');

    const getSaveStatusDisplay = () => {
        switch (saveStatus) {
            case 'saving':
                return (
                    <>
                        <Icon name="Loader2" size="sm" className="animate-spin text-muted" />
                        <span className="text-muted">Saving...</span>
                    </>
                );
            case 'saved':
                return (
                    <>
                        <Icon name="Check" size="sm" className="text-green-500" />
                        <span className="text-muted">Saved</span>
                    </>
                );
            case 'error':
                return (
                    <>
                        <Icon name="AlertTriangle" size="sm" className="text-destructive" />
                        <span className="text-destructive">Save failed</span>
                    </>
                );
            default:
                if (lastSaved) {
                    // Logic to show "Saved Xs ago" could be here, but "Saved" is cleaner
                    return (
                        <>
                            <Icon name="Check" size="sm" className="text-muted" />
                            <span className="text-muted">Saved</span>
                        </>
                    );
                }
                return <span className="text-muted">Auto-save on</span>;
        }
    };

    if (!editor) {
        return <div className="editor-loading">Loading editor...</div>;
    }

    return (
        <div className="editor-panel">
            <div className="editor-header">
                <div className="editor-header-top">
                    <h2 className="editor-title">
                        {isClickableUrl ? (
                            <span
                                className="editor-title-link"
                                onClick={handleTitleClick}
                                title={`Open ${note.key} in new tab`}
                            >
                                {getTitle()}
                                <Icon name="ExternalLink" size="sm" className="editor-title-link-icon" />
                            </span>
                        ) : (
                            getTitle()
                        )}
                        {note.isShared && (
                            <span className="editor-shared-badge" title="Shared via link">
                                SHARED
                            </span>
                        )}
                    </h2>
                    <div className="editor-actions">
                        <span className={`save-status ${saveStatus}`}>
                            {getSaveStatusDisplay()}
                        </span>
                        <button
                            className="save-btn"
                            onClick={handleManualSave}
                            title="Save now"
                        >
                            <Icon name="Save" size="sm" />
                        </button>
                        <ExportDropdown note={note} placement="bottomRight" />
                        <button
                            className="share-btn"
                            onClick={() => onShare?.()}
                            title="Share note"
                        >
                            <Icon name="Link" size="sm" />
                        </button>
                        <button
                            className="sync-btn"
                            onClick={syncNotes}
                            title="Sync from cloud"
                        >
                            <Icon name="RefreshCw" size="sm" />
                        </button>
                    </div>
                </div>
                <div className="editor-meta">
                    {note.isPageNote ? 'Page Note' : 'Site Note'} • {note.domain}
                </div>
            </div>

            <Toolbar editor={editor} />

            {/* Note Metadata - Folder and Tags */}
            <div className="note-metadata">
                <div className="metadata-row">
                    <label>Folder:</label>
                    <FolderPicker
                        value={selectedFolderId}
                        onChange={handleFolderChange}
                    />
                </div>
                <div className="metadata-row">
                    <label>Tags:</label>
                    <TagPicker
                        value={selectedTagIds}
                        onChange={handleTagsChange}
                    />
                </div>
            </div>

            <div className="editor-content">
                <EditorContent editor={editor} />
            </div>

            {/* AI Writing Assistant - floating button */}
            <AIAssistant editor={editor} />
        </div>
    );
}

function parseContent(content) {
    if (!content) {
        return { type: 'doc', content: [] };
    }

    if (typeof content === 'object') {
        if (content.type === 'doc') return content;
        if (content.version === 2 && content.content) {
            return content.content;
        }
        return { type: 'doc', content: [] };
    }

    if (typeof content === 'string') {
        try {
            const parsed = JSON.parse(content);
            if (parsed.type === 'doc') return parsed;
            if (parsed.version === 2 && parsed.content) {
                return parsed.content;
            }
        } catch {
            const trimmed = content.trim();
            if (trimmed.startsWith('<') && /<[a-z][\s\S]*>/i.test(trimmed)) {
                return trimmed;
            }
            return {
                type: 'doc',
                content: content.split('\n').map(line => ({
                    type: 'paragraph',
                    content: line ? [{ type: 'text', text: line }] : undefined,
                })),
            };
        }
    }

    return { type: 'doc', content: [] };
}
